import type { GroupMemberSignUpMethod } from '@group/domain';
import { GroupLicense } from '@group/domain';
import { Button } from '@framework/ui/atoms/Button';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { generateDefaultIcon } from '@framework/utils';
import { AvailableSignUpMethodForm } from './AvailableSignUpMethodForm';
import { GroupLicenseInfo } from '../GroupConsoleLicensePage/GroupLicenseInfo';
import { usePersistedObject } from '@framework/repository';
import type { ModelingFormat } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { PublicIconUploader } from '@framework/utils/PublicIconUploader';
import { toast } from 'react-hot-toast';
import { ImageCropModal } from '@framework/ui/elements/ImageCropModal';
import { GroupConsoleTitle } from '../common';
import { trpcClient } from '@framework/trpcClient';
import { GroupLicenseUsage } from './GroupLicenseUsage';
import { ViewModelIndividualPermissions } from './ViewModelIndividualPermissions';
import { useGroupMemberCount } from '@group/hooks/useGroupMemberCount';
import { useGroupConsoleOutletContext } from '../WithGroupConsoleMenu';
import { DefaultModelingFormatForm } from './DefaultModelingFormatForm';

export const GroupConsolePage: React.FC = () => {
  const { t } = useTranslation();
  const { group } = useGroupConsoleOutletContext();
  const [processing, setProcessing] = useState<boolean>(false);
  const [iconUrl, setIconUrl] = useState<string | null>(group.imageUrl);
  const [name, setName] = useState(group.name.toString());
  const [iconImage, setIconImage] = useState<File | null>(null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);
  const [signUpMethods, setSignUpMethods] = useState(group.setting.availableSignUpMethods);
  const [enableIndividualViewModelPermissions, setEnableIndividualViewModelPermissions] = useState(
    group.setting.enableIndividualViewModelPermissions
  );
  const [defaultModelingFormat, setDefaultModelingFormat] = useState(group.setting.defaultModelingFormat);
  const [license] = usePersistedObject(GroupLicense, RTDBPath.Group.groupLicensePath(group.id));
  const { counts: groupMemberCount } = useGroupMemberCount(group.id);

  const handleChangeName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleUploadImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) setUploadedImage(event.target.files[0]);
  }, []);

  const handleImageClear = useCallback(() => {
    setIconImage(null);
    setIconUrl(null);
  }, []);

  const handleChangeSignUpMethods = useCallback((signUpMethods: GroupMemberSignUpMethod) => {
    setSignUpMethods(signUpMethods);
  }, []);

  const handleChangeDefaultModelingFormat = useCallback((mode: ModelingFormat) => {
    setDefaultModelingFormat(mode);
  }, []);

  const handleSubmit = async () => {
    setProcessing(true);

    const newIconUrl = iconImage ? await PublicIconUploader.uploadGroupIcon(group.id, iconImage) : iconUrl;
    setIconUrl(newIconUrl);

    try {
      await trpcClient.group.updateGroupSettings.mutate({
        groupId: group.id,
        name,
        imageUrl: newIconUrl,
        setting: {
          availableSignUpMethods: signUpMethods.dump(),
          enableIndividualViewModelPermissions,
          defaultModelingFormat,
        },
      });
      toast.success(t('group.consolePage.updateSuccess'));
    } catch (err) {
      toast.error(t('group.consolePage.updateError'));
      console.error(err);
    }

    setProcessing(false);
  };

  const handleCropModalClose = useCallback(() => {
    (document.getElementById('icon-image') as HTMLInputElement).value = '';
    setUploadedImage(null);
    setIsCropModalOpen(false);
  }, []);

  const handleCropModalSubmit = useCallback((image: File) => {
    setIconImage(image);
    setIconUrl(URL.createObjectURL(image));
    setIsCropModalOpen(false);
  }, []);

  useEffect(() => {
    if (uploadedImage) setIsCropModalOpen(true);
  }, [uploadedImage]);

  return (
    <div className="pb-8">
      <div className="mb-4 flex items-center justify-between">
        <GroupConsoleTitle title={t('group.console.nav.settings')} />
        <Button color="brand" onClick={handleSubmit} disabled={processing}>
          {t('group.consolePage.submit')}
        </Button>
      </div>
      {/* アイコン & グループ名 */}
      <div className="mb-4 grow">
        <div className="flex">
          {iconUrl ? (
            <div className="relative mr-4 size-16 shrink-0">
              <div className="absolute size-16 overflow-hidden rounded-full">
                <img className="size-full" src={iconUrl} alt={group.name.toString()} />
              </div>
              <div className="absolute flex size-16 items-start justify-end">
                <div className="relative flex cursor-pointer items-center justify-center" onClick={handleImageClear}>
                  <FontAwesomeIcon icon={faCircle} className="absolute text-2xl text-red-500" />
                  <FontAwesomeIcon icon={faTimes} className="absolute text-xl text-white" />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative mr-4 size-16 shrink-0 overflow-hidden rounded-full">
              <div className="absolute size-full opacity-50">
                {generateDefaultIcon({
                  id: group.id,
                  name: name || group.name.toString(),
                  size: 64,
                })}
              </div>
              <div className="absolute flex size-full items-center justify-center">
                <FontAwesomeIcon icon={faPlus} className="text-2xl text-blue-500" />
              </div>
              <input
                type="file"
                id="icon-image"
                className="absolute size-full cursor-pointer opacity-0"
                accept="image/png, image/jpeg"
                onChange={handleUploadImage}
              />
            </div>
          )}
          <div className="flex w-7/12 flex-col">
            <div className="font-bold">{t('group.consolePage.nameLabel')}</div>
            <input type="text" className="rounded-sm border p-2" value={name} onChange={handleChangeName} autoFocus />
          </div>
        </div>
      </div>

      {/* ログイン方法 */}
      <div className="pb-2 pt-8 font-bold">{t('group.consolePage.signUpMethodSection')}</div>
      <AvailableSignUpMethodForm signUpMethod={signUpMethods} onChange={handleChangeSignUpMethods} />

      {/* ビューモデルごとの権限設定 */}
      <div className="pb-2 pt-8 font-bold">{t('workspace.individualPermission.sectionTitle')}</div>
      <ViewModelIndividualPermissions
        enabled={enableIndividualViewModelPermissions}
        onChange={setEnableIndividualViewModelPermissions}
      />

      {/* デフォルトのモデリングフォーマット設定 */}
      <div className="pb-2 pt-8 font-bold">{t('group.consolePage.defaultModelingFormat')}</div>
      <DefaultModelingFormatForm
        defaultModelingFormat={defaultModelingFormat}
        onChange={handleChangeDefaultModelingFormat}
      />

      {license && (
        <>
          <div className="pb-2 pt-8 font-bold">{t('group.consolePage.licenseInfoSection')}</div>
          <GroupLicenseInfo license={license} />
        </>
      )}

      {license && groupMemberCount && (
        <>
          <div className="pb-2 pt-8 font-bold">{t('group.consolePage.licenseUsageSection')}</div>
          <GroupLicenseUsage licenseCount={license.count} groupMemberCount={groupMemberCount} />
        </>
      )}

      {uploadedImage && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={handleCropModalClose}
          onSubmit={handleCropModalSubmit}
          src={uploadedImage}
        />
      )}
    </div>
  );
};
